/**
 * Helpers for Notes factory methods
 */

import type { IBaseCustomer } from "../../BaseCustomerFactory";
import type Note from "le-coffre-resources/dist/Customer/Note";
import type { ApiPayload, QueryPayload } from "../../../../types";

/**
 * Create GET by UID method for notes
 */
export function createGetByUidMethod(baseCustomer: IBaseCustomer, baseUrl: string) {
	return async (uid: string, q?: QueryPayload): Promise<Note> => {
		const url = new URL(baseUrl.concat(`/${uid}`));
		if (q) baseCustomer.appendJsonSearchParams(url, q);
		try {
			return await baseCustomer.getRequest<Note>(url);
		} catch (err) {
			baseCustomer.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create GET by customer and folder method
 */
export function createGetByCustomerAndFolderMethod(baseCustomer: IBaseCustomer, baseUrl: string) {
	return async (customerUid: string, folderUid: string): Promise<Note[]> => {
		const url = new URL(baseUrl.concat(`/customer/${customerUid}/folders/${folderUid}/`));
		try {
			return await baseCustomer.getRequest<Note[]>(url);
		} catch (err) {
			baseCustomer.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create POST method for notes
 */
export function createPostMethod(baseCustomer: IBaseCustomer, baseUrl: string) {
	return async (note: Partial<Note>): Promise<Note> => {
		const url = new URL(baseUrl);
		const payload: ApiPayload = {
			content: note.content ?? null,
			folder_uid: note.folder?.uid,
			customer_uid: note.customer?.uid,
		};
		try {
			return await baseCustomer.postRequest<Note>(url, payload);
		} catch (err) {
			baseCustomer.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create PUT method for notes
 */
export function createPutMethod(baseCustomer: IBaseCustomer, baseUrl: string) {
	return async (uid: string, note: Partial<Note>): Promise<Note> => {
		const url = new URL(baseUrl.concat(`/${uid}`));
		const payload: ApiPayload = {
			content: note.content ?? null,
		};
		try {
			return await baseCustomer.putRequest<Note>(url, payload);
		} catch (err) {
			baseCustomer.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create DELETE method for notes
 */
export function createDeleteMethod(baseCustomer: IBaseCustomer, baseUrl: string) {
	return async (uid: string): Promise<Note> => {
		const url = new URL(baseUrl.concat(`/${uid}`));
		try {
			return await baseCustomer.deleteRequest<Note>(url);
		} catch (err) {
			baseCustomer.onError(err);
			return Promise.reject(err);
		}
	};
}
